import { type PropsWithChildren, type ReactElement } from 'react'
import {
  // reason: this is the wrapped component which we will allow since this is the exported version
  // eslint-disable-next-line @dhl/dhl-ui/no-restricted-imports
  Divider as MuiDivider,
  type DividerProps,
} from '@mui/material'
import { type SystemCssProperties } from '@dhl-ui/theme'
import type { MinimalSxMargin, MinimalSxPadding } from '../../types'

type DividerBaseProps = Pick<DividerProps, 'variant' | 'component' | 'flexItem'>

type VerticalSx = Pick<
  SystemCssProperties,
  'minHeight' | 'height' | 'displayPrint' | MinimalSxMargin | MinimalSxPadding
> & {
  width?: never
}

type HorizontalSx = Pick<SystemCssProperties, 'width' | 'displayPrint' | MinimalSxMargin | MinimalSxPadding> & {
  height?: never
  minHeight?: never
}

type VerticalProps = {
  /**
   * Plot a vertical line
   * @default horizontal
   */
  orientation: 'vertical'

  sx?: VerticalSx
}

type HorizontalProps = {
  /**
   * Plot a horizontal line
   * @default horizontal
   */
  orientation?: 'horizontal'

  sx?: HorizontalSx
}

type HorizontalDividerProps = PropsWithChildren<DividerBaseProps & HorizontalProps>
type VerticalDividerProps = PropsWithChildren<DividerBaseProps & VerticalProps>

type Props = HorizontalDividerProps | VerticalDividerProps

/**
 * The <Divider> is a simple, horizontal or vertical line used to separate content visually.
 * It helps organize information and establish a clear structure in the user interface,
 * ensuring a clean and intuitive layout.
 *
 *  Demos:
 * - [Divider](https://design-system.accept.cl1.intra.dhlparcel.io/?path=/docs/primitives-divider--docs)
 *
 */

export function Divider(props: HorizontalDividerProps): ReactElement

// reason: TypeScript function overload signature for orientation-specific props.
// eslint-disable-next-line no-redeclare
export function Divider(props: VerticalDividerProps): ReactElement

// reason: TypeScript overload implementation for orientation-specific props.
// eslint-disable-next-line no-redeclare
export function Divider({ orientation = 'horizontal', children, ...props }: Props): ReactElement {
  return (
    <MuiDivider {...props} orientation={orientation}>
      {children}
    </MuiDivider>
  )
}
